"use client"

import * as React from "react"
import { cn } from "cn"

import { prefersReducedMotion } from "../../lib/motion"

const NODE_SPACING_PX = 68
const NODE_JITTER = 0.42
const NEIGHBOR_RADIUS_PX = 112
const SPAWN_EVERY_MS = 1300
const MAX_IDEAS = 5
const GROW_EVERY_MS = { min: 170, max: 280 }
const EDGES_PER_IDEA = { min: 7, max: 15 }
const EDGE_DRAW_MS = 620
const IDEA_FADE_IN_MS = 400
const IDEA_HOLD_MS = 2600
const IDEA_FADE_OUT_MS = 2200
const CONTENT_HALF_WIDTH_PX = 320
const CENTER_DIM = 0.28
const LATENT_NODE_ALPHA = 0.22
const LINE_ALPHA = 0.55
const MAX_PIXEL_RATIO = 1.5

type FieldNode = { x: number; y: number; neighbors: number[] }
type Edge = { from: number; to: number; start: number }
type Idea = {
  seed: number
  born: number
  edges: Edge[]
  lit: Set<number>
  target: number
  nextGrowth: number
  grownAt: number | null
}

type Palette = { line: string; node: string; spark: string }

const random = (min: number, max: number) => min + Math.random() * (max - min)
const smoothstep = (edge0: number, edge1: number, value: number) => {
  const t = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)))
  return t * t * (3 - 2 * t)
}

function buildNodes(width: number, height: number): FieldNode[] {
  const nodes: FieldNode[] = []
  for (let y = NODE_SPACING_PX / 2; y < height + NODE_SPACING_PX; y += NODE_SPACING_PX) {
    for (let x = NODE_SPACING_PX / 2; x < width + NODE_SPACING_PX; x += NODE_SPACING_PX) {
      const jitter = NODE_SPACING_PX * NODE_JITTER
      nodes.push({ x: x + random(-jitter, jitter), y: y + random(-jitter, jitter), neighbors: [] })
    }
  }
  for (let a = 0; a < nodes.length; a++) {
    for (let b = a + 1; b < nodes.length; b++) {
      const nodeA = nodes[a]!
      const nodeB = nodes[b]!
      if (Math.hypot(nodeA.x - nodeB.x, nodeA.y - nodeB.y) < NEIGHBOR_RADIUS_PX) {
        nodeA.neighbors.push(b)
        nodeB.neighbors.push(a)
      }
    }
  }
  return nodes
}

function readPalette(): Palette {
  const styles = getComputedStyle(document.documentElement)
  const dark = document.documentElement.classList.contains("dark")
  const token = (name: string) => styles.getPropertyValue(name).trim()
  return {
    line: token(dark ? "--color-greyscale-600" : "--color-greyscale-400"),
    node: token("--color-greyscale-500"),
    spark: token("--color-primary-500"),
  }
}

function zoneWeight(x: number, width: number) {
  const fromCenter = Math.abs(x - width / 2)
  return CENTER_DIM + (1 - CENTER_DIM) * smoothstep(CONTENT_HALF_WIDTH_PX * 0.5, CONTENT_HALF_WIDTH_PX * 1.6, fromCenter)
}

function pickSeed(nodes: FieldNode[], width: number) {
  for (let attempt = 0; attempt < 12; attempt++) {
    const index = Math.floor(Math.random() * nodes.length)
    if (Math.random() < zoneWeight(nodes[index]!.x, width)) return index
  }
  return Math.floor(Math.random() * nodes.length)
}

function grow(idea: Idea, nodes: FieldNode[], now: number) {
  const frontier = Array.from(idea.lit).filter((index) => nodes[index]!.neighbors.some((n) => !idea.lit.has(n)))
  const from = frontier[Math.floor(Math.random() * frontier.length)]
  if (from === undefined) {
    idea.target = idea.edges.length
    return
  }
  const options = nodes[from]!.neighbors.filter((n) => !idea.lit.has(n))
  const to = options[Math.floor(Math.random() * options.length)]!
  idea.edges.push({ from, to, start: now })
  idea.lit.add(to)
}

function ideaAlpha(idea: Idea, now: number) {
  const fadeIn = Math.min(1, (now - idea.born) / IDEA_FADE_IN_MS)
  if (idea.grownAt === null) return fadeIn
  const fadeOutStart = idea.grownAt + EDGE_DRAW_MS + IDEA_HOLD_MS
  return fadeIn * (1 - Math.min(1, Math.max(0, (now - fadeOutStart) / IDEA_FADE_OUT_MS)))
}

function EmergenceField({ className }: { className?: string }) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)

  React.useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas?.getContext("2d")
    if (!canvas || !context) return

    let width = 0
    let height = 0
    let nodes: FieldNode[] = []
    let latent: HTMLCanvasElement | null = null
    let palette = readPalette()
    let ideas: Idea[] = []
    let lastSpawn = 0
    let frame = 0

    const renderLatent = () => {
      const ratio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO)
      latent = document.createElement("canvas")
      latent.width = width * ratio
      latent.height = height * ratio
      const layer = latent.getContext("2d")
      if (!layer) return
      layer.scale(ratio, ratio)
      layer.fillStyle = palette.node
      for (const node of nodes) {
        layer.globalAlpha = LATENT_NODE_ALPHA * zoneWeight(node.x, width)
        layer.beginPath()
        layer.arc(node.x, node.y, 1, 0, Math.PI * 2)
        layer.fill()
      }
    }

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO)
      width = canvas.clientWidth
      height = canvas.clientHeight
      canvas.width = width * ratio
      canvas.height = height * ratio
      context.setTransform(ratio, 0, 0, ratio, 0, 0)
      nodes = buildNodes(width, height)
      ideas = []
      renderLatent()
    }

    const spawn = (now: number) => {
      if (nodes.length === 0) return
      const seed = pickSeed(nodes, width)
      ideas.push({
        seed,
        born: now,
        edges: [],
        lit: new Set([seed]),
        target: Math.round(random(EDGES_PER_IDEA.min, EDGES_PER_IDEA.max)),
        nextGrowth: now + IDEA_FADE_IN_MS / 2,
        grownAt: null,
      })
    }

    const draw = (now: number) => {
      context.clearRect(0, 0, width, height)
      if (latent) context.drawImage(latent, 0, 0, width, height)
      context.lineWidth = 1
      context.lineCap = "round"

      for (const idea of ideas) {
        const alpha = ideaAlpha(idea, now)
        if (alpha <= 0) continue

        context.strokeStyle = palette.line
        for (const edge of idea.edges) {
          const progress = Math.min(1, (now - edge.start) / EDGE_DRAW_MS)
          const from = nodes[edge.from]!
          const to = nodes[edge.to]!
          const x = from.x + (to.x - from.x) * progress
          const y = from.y + (to.y - from.y) * progress
          context.globalAlpha = alpha * LINE_ALPHA * zoneWeight(from.x, width)
          context.beginPath()
          context.moveTo(from.x, from.y)
          context.lineTo(x, y)
          context.stroke()

          context.fillStyle = progress < 1 ? palette.spark : palette.node
          context.globalAlpha = alpha * (progress < 1 ? 0.9 : 0.5) * zoneWeight(x, width)
          context.beginPath()
          context.arc(x, y, progress < 1 ? 1.6 : 1.3, 0, Math.PI * 2)
          context.fill()
        }

        const seed = nodes[idea.seed]!
        const pulse = 0.5 + 0.5 * Math.sin((now - idea.born) / 420)
        context.fillStyle = palette.spark
        context.globalAlpha = alpha * (0.12 + 0.12 * pulse) * zoneWeight(seed.x, width)
        context.beginPath()
        context.arc(seed.x, seed.y, 6 + 3 * pulse, 0, Math.PI * 2)
        context.fill()
        context.globalAlpha = alpha * 0.85 * zoneWeight(seed.x, width)
        context.beginPath()
        context.arc(seed.x, seed.y, 2, 0, Math.PI * 2)
        context.fill()
      }
      context.globalAlpha = 1
    }

    const step = (now: number) => {
      if (now - lastSpawn > SPAWN_EVERY_MS && ideas.length < MAX_IDEAS) {
        spawn(now)
        lastSpawn = now
      }
      for (const idea of ideas) {
        if (idea.grownAt !== null) continue
        if (idea.edges.length >= idea.target) {
          idea.grownAt = now
          continue
        }
        if (now >= idea.nextGrowth) {
          grow(idea, nodes, now)
          idea.nextGrowth = now + random(GROW_EVERY_MS.min, GROW_EVERY_MS.max)
        }
      }
      ideas = ideas.filter((idea) => idea.grownAt === null || ideaAlpha(idea, now) > 0)
      draw(now)
      frame = requestAnimationFrame(step)
    }

    const renderStill = () => {
      const now = performance.now()
      for (let count = 0; count < MAX_IDEAS - 1; count++) {
        spawn(now - EDGE_DRAW_MS)
        const idea = ideas[ideas.length - 1]
        if (!idea) return
        while (idea.edges.length < idea.target) grow(idea, nodes, now - EDGE_DRAW_MS)
        idea.grownAt = now
      }
      draw(now)
    }

    const resizeObserver = new ResizeObserver(() => {
      resize()
      if (prefersReducedMotion()) renderStill()
    })
    const themeObserver = new MutationObserver(() => {
      palette = readPalette()
      renderLatent()
      if (prefersReducedMotion()) draw(performance.now())
    })

    resize()
    resizeObserver.observe(canvas)
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] })
    if (prefersReducedMotion()) renderStill()
    else frame = requestAnimationFrame(step)

    return () => {
      cancelAnimationFrame(frame)
      resizeObserver.disconnect()
      themeObserver.disconnect()
    }
  }, [])

  return <canvas ref={canvasRef} aria-hidden="true" data-slot="emergence-field" className={cn("size-full", className)} />
}

export { EmergenceField }
